import { ReactNode, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Filter } from "../types";
import { formatCurrency } from "../../_app/utils/format";
import { formatCostCentreCode } from "../../account/utils";
import { formatPeriodToMonthYear } from "../../billing/utils";
import { filtersIdMap, formatMultipleBillPeriods } from "../utils";
import { createStylesheet } from "../../_app/utils/styles";
import { UIChip } from "../../_app/components";

const MULTI_VALUE_FILTER_TYPES = new Set(["TEXT", "TEXT_CASE_SENSITIVE", "IDENTIFIER", "ENUM", "AMOUNT"]);

// A date range is stored as two URL params with the same key (from, to). The chip shows them as one
// value, so deleting it removes the whole range rather than leaving an orphaned half in the URL.
const isDateRange = (item: Filter) => item.type === "DATE_RANGE";

interface FilterItem extends Filter {
  key: string;
  value: string;
}

interface Props {
  filters: Filter[];
}

interface MultiValueChipProps {
  item: FilterItem;
  prefix: string;
  values: string[];
  formatFilterValue: (item: FilterItem, value?: string) => string;
  onDelete: (item: FilterItem) => void;
  onDeleteValue: (item: FilterItem, index: number) => void;
  classes: ReturnType<typeof useStyles>;
}

const MultiValueFilterChip = ({
  item,
  prefix,
  values,
  formatFilterValue,
  onDelete,
  onDeleteValue,
  classes,
}: MultiValueChipProps) => {
  const valueChips = values.reduce<ReactNode[]>((chips, value, index) => {
    const valueLabel = formatFilterValue(item, value);
    if (!valueLabel) return chips;

    chips.push(
      <UIChip
        key={`${item.key}-${index}`}
        variant="outlined"
        size="small"
        label={valueLabel}
        onDelete={(event) => {
          event.stopPropagation();
          event.preventDefault();
          if (isDateRange(item)) onDelete(item);
          else onDeleteValue(item, index);
        }}
        className={classes.innerChip}
        data-cy={`added-filter-${item.key}-value-${index}`}
        data-testid={`added-filter-${item.key}-value-${index}`}
      />,
    );
    return chips;
  }, []);

  if (!valueChips.length) return null;

  const groupChip = (
    <UIChip
      key={`${item.key}-group`}
      size="small"
      label={prefix}
      onDelete={!item.disableDelete && values.length > 1 ? () => onDelete(item) : undefined}
      className={classes.multiValueGroupLabel}
      data-cy={`added-filter-${item.key}-label`}
    />
  );

  return (
    <li className={classes.chip} key={item.key}>
      <UIChip
        variant="outlined"
        size="small"
        label={
          <span className={classes.multiValueLabel}>
            {groupChip}
            <span className={classes.multiValueValues}>{valueChips}</span>
          </span>
        }
        className={classes.multiValueGroupChip}
        data-cy={`added-filter-${item.key}`}
      />
    </li>
  );
};

const AddedFilters = ({ filters }: Props) => {
  const classes = useStyles();
  const location = useLocation();
  const navigate = useNavigate();

  const items = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const result: FilterItem[] = [];
    const valuesByKey = new Map<string, string[]>();

    params.forEach((value, key) => {
      const currentValues = valuesByKey.get(key) ?? [];
      currentValues.push(value);
      valuesByKey.set(key, currentValues);
    });

    valuesByKey.forEach((values, key) => {
      const filter = filters?.find((f) => f.id === key && !f.options?.HIDDEN);
      if (filter) {
        const fallbackValue = values.length ? values[values.length - 1] : "";
        result.push({ ...filter, key, value: fallbackValue, selectedValues: values });
      }
    });

    return result;
  }, [location.search, filters]);

  const deleteFilter = (item: FilterItem) => {
    const params = new URLSearchParams(location.search);
    params.delete(item.key);
    navigate({ search: params.toString() }, { replace: true });
  };

  const deleteFilterValue = (item: FilterItem, valueIndex: number) => {
    const params = new URLSearchParams(location.search);
    const existingValues = params.getAll(item.key);
    if (!existingValues.length) return;
    if (valueIndex < 0 || valueIndex >= existingValues.length) return;

    const nextValues = existingValues.filter((_, index) => index !== valueIndex);
    params.delete(item.key);
    nextValues.forEach((value) => params.append(item.key, value));
    navigate({ search: params.toString() }, { replace: true });
  };

  const normalizeValue = (value: string) => value.split("_").join(" ").replace("equal", "equals");

  const formatFilterValue = (item: FilterItem, valueOverride?: string) => {
    const baseValue = valueOverride ?? item.selectedValues?.[0] ?? item.value ?? "";
    if (!baseValue) return "";

    switch (item.key) {
      case filtersIdMap.COST:
      case filtersIdMap.ALLOCATED_COST:
        return normalizeValue(baseValue).replace(/\d+(\.\d+)?/g, (match) => formatCurrency(match));

      case filtersIdMap.BILL_PERIOD:
      case filtersIdMap.SERVICES_BILL_PERIOD:
      case filtersIdMap.BILL_PERIOD_70:
        return formatMultipleBillPeriods(baseValue);

      case filtersIdMap.PERIOD_RANGE: {
        const [start, end] = baseValue.split(" - ");
        return `${formatPeriodToMonthYear(start)} - ${formatPeriodToMonthYear(end)}`;
      }

      case filtersIdMap.CALL_DURATION:
        return normalizeValue(baseValue).split(",").join(" and ");

      case filtersIdMap.DATA_RANGE:
        return baseValue.split(",").join(" to ");

      case filtersIdMap.COST_CENTRE:
      case filtersIdMap.COST_CENTRE_23:
      case filtersIdMap.COST_CENTRE_34:
      case filtersIdMap.ASSETS_COST_CENTRE:
      case filtersIdMap.COST_CENTRE_54:
        return baseValue.split(",").map(formatCostCentreCode).join(", ");

      case filtersIdMap.STATUS:
        return baseValue === "true" ? item.label ?? "" : "";

      default: {
        const formattedValue = item.valueDisplayFunc ? item.valueDisplayFunc(baseValue) : baseValue;
        return formattedValue?.split("_").join(" ") ?? "";
      }
    }
  };
  const getFilterValues = (item: FilterItem): string[] => {
    const selectedValues = item.selectedValues ?? [];

    if (item.type && MULTI_VALUE_FILTER_TYPES.has(item.type)) {
      return selectedValues.filter((value) => value !== "");
    }

    if (isDateRange(item)) {
      const range = selectedValues.filter((value) => value !== "");
      return range.length ? [range.join(",")] : [];
    }

    if (selectedValues.length) {
      return [selectedValues[selectedValues.length - 1]];
    }

    return item.value ? [item.value] : [];
  };

  const renderFilterItem = (item: FilterItem) => {
    const prefix = item.label ?? item.name;
    if (!prefix) return null;

    const values = getFilterValues(item);
    if (!values.length) return null;

    return (
      <MultiValueFilterChip
        key={item.key}
        item={item}
        prefix={prefix}
        values={values}
        formatFilterValue={formatFilterValue}
        onDelete={deleteFilter}
        onDeleteValue={deleteFilterValue}
        classes={classes}
      />
    );
  };

  const renderedItems = items.map((item) => renderFilterItem(item)).filter(Boolean);

  if (!renderedItems.length) return null;

  return (
    <ul className={classes.ctr} data-cy="added-filters">
      {renderedItems}
    </ul>
  );
};

const useStyles = createStylesheet((theme) => ({
  ctr: {
    display: "flex",
    minHeight: "32px",
    flexDirection: "row",
    flexWrap: "wrap",
    gap: "6px",
    width: "100%",
    listStyleType: "none",
    padding: "3px",
    marginLeft: "0px",
    marginRight: "0px",
    border: "1px solid",
    borderColor: theme.palette.grey[100],
    backgroundColor: theme.palette.grey[50],
    borderRadius: theme.spacing(2),
    alignContent: "center",
  },
  chip: {
    maxWidth: "100%",
  },
  multiValueGroupChip: {
    maxWidth: "100%",
    height: "auto",
    backgroundColor: theme.palette.background.paper,
    "& .MuiChip-label": {
      padding: theme.spacing(0.5, 0.75),
      display: "inline-flex",
      alignItems: "flex-start",
      gap: theme.spacing(1),
      flexWrap: "nowrap",
    },
  },
  multiValueLabel: {
    display: "inline-flex",
    alignItems: "center",
    gap: theme.spacing(1),
    flexWrap: "nowrap",
  },
  multiValueGroupLabel: {
    fontWeight: 600,
  },
  multiValueValues: {
    display: "inline-flex",
    flexWrap: "wrap",
    gap: theme.spacing(0.5),
    paddingLeft: theme.spacing(0.5),
  },
  innerChip: {
    backgroundColor: theme.palette.background.paper,
  },
}));

export default AddedFilters;
